'use client';

import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/navigation';
import { useDismiss } from './hooks/useDismiss';
import { Menubar } from './MenuBar';
import { MobileMenu } from './MobileMenu';
import { RightPanels } from './RightPanels';
import type { MegaMenuProps } from './types/menu';
import { useNavigationMenu } from '@/hooks/useNavigationMenu';
import { useLocale } from '@/app/[lang]/providers/LocaleProvider';

export default function MegaMenu({
  open,
  setOpen,
  initialMenuId,
  onNavigate,
}: MegaMenuProps) {
  const { data: menus = [], isLoading, isError } = useNavigationMenu();
  const locale = useLocale();
  const router = useRouter();

  const [activeMenuId, setActiveMenuId] = useState<number | null>(
    initialMenuId ?? null
  );

  const containerRef = useRef<HTMLElement | null>(null);
  const baseId = useId();

  useDismiss(open, containerRef, () => setOpen(false));

  const focusItem = (idx: number) => {
    if (idx < 0) return;
    const item =
      containerRef.current?.querySelector<HTMLLIElement>(
        `[data-menu-index="${idx}"]`
      );
    item?.focus();
  };

  useEffect(() => {
    if (!menus.length) return;
    if (activeMenuId === null) {
      setActiveMenuId(initialMenuId ?? menus[0].id);
      return;
    }
    const exists = menus.some((menu) => menu.id === activeMenuId);
    if (!exists) {
      setActiveMenuId(menus[0].id);
    }
  }, [menus, activeMenuId, initialMenuId]);

  // Auto-focus root item saat pertama kali dibuka
  const openedOnce = useRef(false);
  useEffect(() => {
    if (!open) return;
    const activeIndex = menus.findIndex((menu) => menu.id === activeMenuId);
    if (!openedOnce.current && activeIndex >= 0) {
      openedOnce.current = true;
      requestAnimationFrame(() => focusItem(activeIndex));
    }
    return () => {
      openedOnce.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, menus, activeMenuId]);

  const onKeyNav = useCallback(
    (e: React.KeyboardEvent<HTMLLIElement>, index: number) => {
      const last = menus.length - 1;
      const move = (next: number) => {
        const nextMenu = menus[next];
        if (!nextMenu) return;
        setActiveMenuId(nextMenu.id);
        focusItem(next);
      };
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          move(index === last ? 0 : index + 1);
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          move(index === 0 ? last : index - 1);
          break;
        case 'Home':
          e.preventDefault();
          move(0);
          break;
        case 'End':
          e.preventDefault();
          move(last);
          break;
        default:
          break;
      }
    },
    [menus]
  );

  if (!open) return null;

  const activeMenu =
    menus.find((menu) => menu.id === activeMenuId) ?? menus[0] ?? null;

  const columnClass =
    (activeMenu?.children.length ?? 0) <= 4 ? 'columns-1' : 'columns-2';

  return (
    <nav
      ref={containerRef}
      aria-label="Site navigation menu"
      className="absolute top-16 left-1/2 z-50 w-full max-w-6xl -translate-x-1/2 rounded-b-xl bg-white shadow-lg ring-1 ring-black/5 md:top-20"
    >
      {menus.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">
          {isLoading && !isError ? 'Loading navigation…' : 'Navigation unavailable.'}
        </div>
      ) : (
        <>
          {/* Mobile: accordion with collapsible submenus */}
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto rounded-b-xl bg-[url('/images/mesh-103.png')] bg-cover bg-center px-4 py-2 md:hidden">
            <MobileMenu
              menus={menus}
              onNavigate={() => {
                onNavigate?.();
                setOpen(false);
              }}
            />
          </div>

          {/* Desktop: two-column mega menu */}
          <div className="hidden md:grid md:grid-cols-3">
            <div className="col-span-1 border-b border-[#EAECF0] bg-[url('/images/mesh-119.png')] bg-no-repeat bg-right py-4 pl-4 md:border-b-0 md:border-r md:py-8 md:pl-8">
              <Menubar
                menus={menus}
                activeId={activeMenu?.id ?? null}
                setActiveId={(id) => setActiveMenuId(id)}
                onKeyNav={onKeyNav}
                id={`${baseId}-menubar`}
                onLeafClick={(path) => {
                  router.push(`/${locale}${path}`);
                  onNavigate?.();
                  setOpen(false);
                }}
              />
            </div>

            <RightPanels
              activeMenu={activeMenu}
              baseId={baseId}
              onItemClick={() => {
                onNavigate?.();
                setOpen(false);
              }}
              columnClass={columnClass}
            />
          </div>
        </>
      )}
    </nav>
  );
}
